import React, { useEffect, useState } from "react";
import { Club } from "api/clubs";
import { DateSelect, GridTextField } from "components";
import { Challenge, ChallengePostParams, postAdminChallenges } from "api/admin/challenges";
import { useSnackbar } from "notistack";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid } from "@material-ui/core";
import moment from "moment";
import i18n from "./i18n";

interface CreateChallengeDialogProps {
    challengeData: Challenge,
    club: Club | null,
    show: boolean,
    onClose: () => void,
    onRefreshTable: () => void,
}

const CreateChallengeDialog = ({ challengeData, onRefreshTable, club, show, onClose }: CreateChallengeDialogProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    const [isLoading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string | undefined>();
    const [challengeName, setChallengeName] = useState("");
    const [description, setDescription] = useState<string>("");
    const [blueprintChallengeId, setBlueprintChallengeId] = useState("");
    const [clubSite, setClubSite] = useState<string>("");
    const [enableAudienceGroups, setEnableAudienceGroups] = useState(false);

    useEffect(() => {
        if (show) {
            if (challengeData?.endDate) {
                setEndDate((moment(challengeData.endDate).format("DD.MM.YYYY HH:ii:ss")));
            }
            setChallengeName(challengeData.challengeName);
            setDescription(challengeData.description || "");
            setBlueprintChallengeId(challengeData.id);
        }
    }, [show]);

    const createChallenge = async () => {
        setLoading(true);
        const audienceGroups = [{
            clubId: club?.id,
            clubSite,
        }];
        let params: ChallengePostParams = {
            clubId: club?.id,
            startDate,
            endDate,
            challengeName,
            blueprintChallengeId,
            description,
        };
        if (enableAudienceGroups) {
            params = { ...params, audienceGroups };
        }
        try {
            await postAdminChallenges(params);
            enqueueSnackbar(i18n.successMsg, { variant: "success" });
            onClose();
            onRefreshTable(); // fetch update data
        } catch (err: any) {
            if (err.type === "invalidParameters") {
                enqueueSnackbar(i18n.invalidParameter, { variant: "error" });
            } else {
                displayError(err);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            fullWidth
            open={show}
            onClose={onClose}
        >
            <DialogTitle>{i18n.createChallenge}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <DateSelect
                            label={i18n.startDate}
                            value={startDate}
                            onChange={(m) => setStartDate(m?.toISOString() || "")}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateSelect
                            label={i18n.endDate}
                            value={endDate}
                            onChange={(m) => setEndDate(m?.toISOString() || "")}
                        />
                    </Grid>

                    <GridTextField
                        label={i18n.title}
                        maxLength={128}
                        size={12}
                        value={challengeName}
                        onChange={setChallengeName}
                    />
                    <GridTextField
                        label={i18n.description}
                        maxLength={255}
                        size={12}
                        value={description}
                        onChange={setDescription}
                    />
                </Grid>
                {/* allow audience groups */}
                <Grid item spacing={2} xs={6} style={{ marginTop: 10 }}>
                    <FormControl>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={enableAudienceGroups}
                                    color="primary"
                                    onChange={(e) => setEnableAudienceGroups(e.target.checked)}
                                />
                            )}
                            label={i18n.audienceGroups}
                        />
                    </FormControl>
                </Grid>
                {enableAudienceGroups && (
                    <div style={{ marginTop: 10 }}>
                        <GridTextField
                            disabled
                            label={i18n.club}
                            maxLength={255}
                            size={12}
                            value={club?.clubName || ""}
                            onChange={setDescription}
                        />
                        <div style={{ marginTop: 16 }}>
                            <GridTextField
                                label={i18n.clubSite}
                                maxLength={50}
                                size={12}
                                value={clubSite}
                                onChange={setClubSite}
                            />
                        </div>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" disabled={isLoading || !startDate} onClick={createChallenge}>
                    {i18n.createChallenge}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateChallengeDialog;
